import React, { useCallback, useMemo } from 'react';
import { Specialist, SpecialistCardWithScheduleSkeleton } from '@mindly/ui-components';

import { useSelector } from 'react-redux';
import { isLoadingSpecialistByIDSelector } from 'src/redux/specialists/selector';
import { useFavouriteSpecialist } from 'src/hooks/useFavouriteSpecialist';
import { PrivateRoutes } from 'src/routes/navigation';
import { TranslatedSpecialistCardListWidget } from 'src/translated-components';
import { useAppointment } from 'src/hooks/useAppointment/useAppointment';
import { userSelector } from 'src/redux/user/selector';
import { ImpactStyle } from '@capacitor/haptics';
import { useHistory } from 'react-router';
import { useAnalytics } from 'src/shared';
import { useFilters } from 'src/shared/hooks/useFilters';
import { getSpecialistPosition } from 'src/shared/utils/specialist';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import PsychologistItem from './PsychologistItem';

type Props = {
  isLoading: boolean;
  specialistsList: Specialist[];
};

/**
 * New active psychologists
 */
const PsychologistListItems: React.FC<Props> = ({ isLoading, specialistsList }) => {
  const { isFavouriteSpecialist, toggleFavourite } = useFavouriteSpecialist();
  const history = useHistory();
  const currentUser = useSelector(userSelector);
  const isBookingLoading = useSelector(isLoadingSpecialistByIDSelector);
  const { trackPredefinedEvent } = useAnalytics();
  const { filtersVersion } = useFilters();
  const newList = useFeatureIsOn('specialist-list-v3');

  const onCardClickHandler = useCallback(
    async (specialistID: string) => {
      isomorficHapticsImpact({ style: ImpactStyle.Light });
      const specialist = specialistsList.find((el) => el.id === specialistID);
      if (specialist) {
        const specialistOrderPosition = getSpecialistPosition(specialistID, specialistsList);
        trackPredefinedEvent('SPECIALIST_LIST_SPECIALIST_PROFILE_CLICKED', {
          specialist_id: specialistID,
          specialist_name: `${specialist?.first_name} ${specialist?.last_name}`,
          specialist_order_position: specialistOrderPosition,
          specialist_list_algorithm_version: filtersVersion,
        });
      }
      history.push(PrivateRoutes.PSYCHOLOGIST_PROFILE_TAB.replace(':id', specialistID));
    },
    [filtersVersion, trackPredefinedEvent]
  );

  const { pushToAppointmentPage } = useAppointment();

  const onChooseVisitTime = useCallback(
    (specialist: Specialist) => {
      pushToAppointmentPage(specialist.id);
    },
    [pushToAppointmentPage]
  );

  const handleToggleFavourite = (specialist: Specialist) => {
    toggleFavourite(specialist);
  };

  const list = useMemo(
    () =>
      specialistsList?.map((specialist) => ({
        ...specialist,
        isFavourite: isFavouriteSpecialist(specialist.id),
      })) || [],
    [specialistsList, isFavouriteSpecialist]
  );

  const skeletonList = useMemo(
    () => new Array(5).fill(0).map((_, index) => <SpecialistCardWithScheduleSkeleton key={index} />),
    []
  );

  if (newList) {
    return (
      <div className="px-5">
        {isLoading
          ? skeletonList
          : list.map((specialist) => <PsychologistItem specialist={specialist} key={specialist.id} />)}
      </div>
    );
  }

  return (
    <div className="px-5">
      <TranslatedSpecialistCardListWidget
        specialists={list}
        onToggleFavourite={handleToggleFavourite}
        onCardClick={onCardClickHandler}
        onChooseVisitTime={onChooseVisitTime}
        isLoading={isLoading}
        currentUser={currentUser as any}
        isBookingLoading={isBookingLoading}
      />
    </div>
  );
};

export default PsychologistListItems;
